import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SyncIcon from "@mui/icons-material/Sync";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Box,
  Button,
  Card,
  Chip,
  Link,
  Stack,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ManifestComplianceEscalation } from "generated-graphql/graphql";
import { useCallback } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { findManifestInRcraInfo } from "util/encampy/openManifestInRcraInfo";
import { runMultiPhaseAutomation } from "util/encampy/runMultiPhaseAutomation";

import {
  type ComplianceItem,
  type ComplianceItemEpa,
  type ComplianceItemMissing,
  type DeadlineBand,
  getDeadlineBand,
  getDeadlineDisplay,
} from "./CompliancePane.helpers";
import { getStatusColor, getStatusLabel } from "./DiscrepancyReviewDialog.helpers";
import { getEManifestComplianceDisplay } from "./eManifestComplianceDisplay";

// Figma --primary/main differs from theme.primary.main; remove once theme has a token for it.
const FIGMA_PRIMARY_BLUE = "#2196F3";

function rcraInfoSiteDraftsUrl(epaId: string): string {
  return `https://rcrainfo.epa.gov/rcrainfoprod/action/industry/secured/e-manifest/drafts/${epaId}#!/`;
}

type EmanifestNavigationResult = "navigated" | "fallback-opened" | "fallback-blocked";

async function openManifestWithEncampyOrFallback(
  epaId: string,
  mtn: string | null
): Promise<EmanifestNavigationResult> {
  const draftsUrl = rcraInfoSiteDraftsUrl(epaId);

  // Without an MTN the chain would type an empty filter and click the first
  // View link, opening an unrelated manifest.
  if (!mtn) {
    const opened = window.open(draftsUrl, "_blank", "noopener,noreferrer");
    return opened ? "fallback-opened" : "fallback-blocked";
  }

  try {
    await runMultiPhaseAutomation(draftsUrl, async (runner) => {
      await runner.run((tab) => findManifestInRcraInfo(tab, mtn));
    });
    return "navigated";
  } catch (err) {
    console.warn("Encampy RCRAInfo manifest navigation failed; opening fallback tab:", err);
    // User-gesture token was consumed by the preceding await, so popup blockers may return null.
    const opened = window.open(draftsUrl, "_blank", "noopener,noreferrer");
    return opened ? "fallback-opened" : "fallback-blocked";
  }
}

export interface ComplianceCardProps {
  item: ComplianceItem;
  now: Date;
  activePreviewDocId?: string | null;
  onViewDocument: (documentId: string, fileExtension: string | null) => void;
  onResolveInEManifest: (discrepancyId: string) => void;
  onAddProofOfContact: (processedManifestId: string) => void;
  onCreateExceptionReport: (processedManifestId: string) => void;
  creatingExceptionReport?: boolean;
}

function bandColor(theme: Theme, band: DeadlineBand): string {
  switch (band) {
    case "crit":
      return theme.palette.error.dark;
    case "warn":
      return theme.palette.warning.main;
    case "due":
      return FIGMA_PRIMARY_BLUE;
  }
}

function DeadlineDot({ color }: { color: string }) {
  return (
    <Box
      sx={{
        width: "0.75rem",
        height: "0.75rem",
        borderRadius: "50%",
        flexShrink: 0,
        // Radial highlight upper-left fades to transparent, base color underneath.
        background: `radial-gradient(circle at 32% 30%, ${alpha("#fff", 0.7)} 0%, ${alpha("#fff", 0.25)} 22%, ${alpha("#fff", 0)} 50%), ${color}`,
        boxShadow: `inset 0 -1px 1px ${alpha("#000", 0.18)}`,
      }}
    />
  );
}

/**
 * Card header: manifest identity on the left, deadline pill on the right. The
 * per-card kind label ("EPA CORRECTION REQUEST" / "MISSING E-MANIFEST") is
 * intentionally gone — each card lives under a section with the same title.
 */
function HeaderRow({ item, now }: { item: ComplianceItem; now: Date }) {
  const theme = useTheme();
  const band = getDeadlineBand(item, now);
  const color = bandColor(theme, band);
  // A resolved correction has no live deadline, so hide the urgency pill — the
  // status chip in the actions row communicates the resolved state instead.
  const showDeadline = item.status !== "resolved";
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
      <ManifestLine item={item} />
      {showDeadline && (
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0, pt: "2px" }}>
          <DeadlineDot color={color} />
          <Typography variant="body2" sx={{ color }}>
            {getDeadlineDisplay(item, now)}
          </Typography>
        </Stack>
      )}
    </Stack>
  );
}

function ManifestLine({ item }: { item: ComplianceItem }) {
  const tracking = item.manifestTrackingNumber ?? item.processedManifestId.slice(0, 8);
  const route =
    item.generatorName && item.tsdfName
      ? `${item.generatorName} → ${item.tsdfName}`
      : (item.generatorName ?? item.tsdfName ?? "");
  // Only the tracking number carries weight; the facility route reads as
  // supporting detail.
  return (
    <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
      <Box component="span" sx={{ fontWeight: 600 }}>
        {tracking}
      </Box>
      {route ? ` • ${route}` : null}
    </Typography>
  );
}

function MissingBody({
  item,
  now,
  onAddProofOfContact,
  onCreateExceptionReport,
  creatingExceptionReport,
}: {
  item: ComplianceItemMissing;
  now: Date;
  onAddProofOfContact: (processedManifestId: string) => void;
  onCreateExceptionReport: (processedManifestId: string) => void;
  creatingExceptionReport?: boolean;
}) {
  const theme = useTheme();
  const { tenantId } = useParams<{ tenantId: string }>();
  const proofDocId = item.transporterContactProofDocumentId;
  const proofDate = item.transporterContactedAt?.slice(0, 10);
  const proofLabel = proofDate ?? "View document";
  const hasContact = item.transporterContactedAt != null;
  const submissionId = item.exceptionReportSubmissionId;
  const exceptionEligible = item.escalation === ManifestComplianceEscalation.Overdue_60;
  // Fulfillment actions live on the row they fulfill; hide them once resolved.
  const showActions = item.status !== "resolved";
  // The regulatory requirement for the current escalation level (e.g. "45+ days
  // since shipment — contact the transporter/TSDF"), flipping to the action-taken
  // variant once the user has responded. Same source of truth as the manifest list.
  // Hidden on resolved rows (mirroring the deadline pill): escalation persists
  // after auto-resolve, so the requirement copy would falsely demand action.
  const requirement =
    item.status !== "resolved"
      ? getEManifestComplianceDisplay({
          eManifestComplianceEscalation: item.escalation,
          exceptionReportSubmission: item.exceptionReportSubmissionId
            ? { status: item.exceptionReportSubmissionStatus }
            : null,
          transporterContactedAt: item.transporterContactedAt,
        })
      : null;
  return (
    <Stack spacing={0.75}>
      {item.shippedDate && (
        <Typography variant="caption" color="text.secondary">
          Shipped: {item.shippedDate}
        </Typography>
      )}
      {requirement && (
        // The card's call to action — same color as the deadline pill so the
        // urgency and the required step read as one message.
        <Typography
          variant="body2"
          sx={{ fontWeight: 500, color: bandColor(theme, getDeadlineBand(item, now)) }}
        >
          {requirement.tooltipText}
        </Typography>
      )}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ minHeight: 30 }}>
        <Typography variant="body2">
          Proof of contact:{" "}
          {proofDocId && tenantId ? (
            <Link component={RouterLink} to={`/o/${tenantId}/documents/${proofDocId}`}>
              {proofLabel}
            </Link>
          ) : hasContact ? (
            (proofDate ?? "recorded")
          ) : (
            "–"
          )}
        </Typography>
        {showActions && (
          <Button
            size="small"
            variant={hasContact ? "text" : "outlined"}
            onClick={() => onAddProofOfContact(item.processedManifestId)}
          >
            {hasContact ? "Re-contact transporter" : "Add proof of contact"}
          </Button>
        )}
      </Stack>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ minHeight: 30 }}>
        <Typography variant="body2">
          Exception report:{" "}
          {submissionId && tenantId ? (
            <Link component={RouterLink} to={`/o/${tenantId}/waste/submissions/${submissionId}`}>
              {item.exceptionReportTitle ?? "submission"}
            </Link>
          ) : (
            "–"
          )}
        </Typography>
        {showActions && !submissionId && (
          <Tooltip
            placement="top"
            title={
              exceptionEligible
                ? ""
                : "Available once the shipment passes the 60-day exception-report threshold."
            }
          >
            <span>
              <Button
                size="small"
                variant="outlined"
                disabled={!exceptionEligible || !!creatingExceptionReport}
                onClick={() => onCreateExceptionReport(item.processedManifestId)}
              >
                Create exception report
              </Button>
            </span>
          </Tooltip>
        )}
      </Stack>
    </Stack>
  );
}

/** Quiet utility button: opens the manifest in RCRAInfo. Lives in the actions row. */
function EmanifestLinkButton({ item }: { item: ComplianceItem }) {
  const alerts = useAlerts();
  const handleClick = useCallback(async () => {
    if (!item.epaId) return;
    const result = await openManifestWithEncampyOrFallback(item.epaId, item.manifestTrackingNumber);
    if (result === "fallback-blocked") {
      alerts.error(
        "Your browser blocked the RCRAInfo tab. Allow pop-ups for this site and try again."
      );
    }
  }, [item.epaId, item.manifestTrackingNumber, alerts]);

  if (!item.epaId) return null;
  return (
    <Button
      size="small"
      variant="text"
      startIcon={<OpenInNewIcon />}
      onClick={handleClick}
      sx={{ color: "text.secondary" }}
    >
      Open in e-Manifest
    </Button>
  );
}

function EpaBody({ item }: { item: ComplianceItemEpa }) {
  const theme = useTheme();
  return (
    <Stack spacing={1}>
      <Typography variant="caption" color="text.secondary">
        Requested: {item.requestedAt} by {item.initiator}
        <br />
        Deadline: {item.deadline}
      </Typography>
      <Box sx={{ borderLeft: `1px solid ${theme.palette.grey[400]}`, pl: 1 }}>
        {item.sections.map((s, i) => (
          <Typography
            key={`${i}-${s.sectionNumber}`}
            variant="caption"
            sx={{ color: "text.primary", display: "block" }}
          >
            <strong>§ {s.sectionNumber} </strong>
            <span>– "{s.comments}"</span>
          </Typography>
        ))}
      </Box>
    </Stack>
  );
}

function StatusChip({ item }: { item: ComplianceItem }) {
  return (
    <Chip
      size="small"
      label={getStatusLabel(item.status)}
      color={getStatusColor(item.status)}
      variant="outlined"
      sx={{ bgcolor: "background.paper" }}
    />
  );
}

function ActionsRow({
  item,
  isPreviewedDoc,
  onViewDocument,
  onResolveInEManifest,
}: Pick<ComplianceCardProps, "item" | "onViewDocument" | "onResolveInEManifest"> & {
  isPreviewedDoc: boolean;
}) {
  const theme = useTheme();
  const encampBlue = theme.palette.encampBlue.main;
  const viewDoc = (
    <Button
      key="view"
      size="small"
      variant="text"
      startIcon={<VisibilityIcon />}
      disabled={!item.sourceDocumentId}
      onClick={() => {
        if (item.sourceDocumentId) {
          onViewDocument(item.sourceDocumentId, item.sourceDocumentFileExtension);
        }
      }}
      sx={{
        bgcolor: isPreviewedDoc ? alpha(FIGMA_PRIMARY_BLUE, 0.04) : "transparent",
        color: FIGMA_PRIMARY_BLUE,
        "&:hover": {
          bgcolor: isPreviewedDoc
            ? alpha(FIGMA_PRIMARY_BLUE, 0.08)
            : alpha(FIGMA_PRIMARY_BLUE, 0.04),
        },
      }}
    >
      {isPreviewedDoc ? "Viewing document" : "View document"}
    </Button>
  );

  // Missing e-Manifest cards fulfill their actions inline on the proof /
  // exception rows (see MissingBody), so their only trailing element is the
  // status chip once no longer unresolved.
  let trailing: React.ReactNode = null;
  if (item.status !== "unresolved") {
    trailing = <StatusChip item={item} />;
  } else if (item.kind === "epaCorrection") {
    trailing = (
      <Tooltip
        placement="top"
        title={
          <Typography variant="caption" sx={{ display: "block", maxWidth: "17.5rem" }}>
            Marks this correction as being handled in RCRAInfo (e.g. you're updating the manifest
            values directly in EPA's portal). The row stays in <em>Pending</em> until the corrected
            data syncs back through the pipeline and either resolves automatically or reopens with
            the new values.
          </Typography>
        }
      >
        <Button
          size="small"
          variant="contained"
          startIcon={<SyncIcon />}
          onClick={() => onResolveInEManifest(item.id)}
          sx={{
            bgcolor: encampBlue,
            color: "common.white",
            boxShadow: 2,
            "&:hover": { bgcolor: alpha(encampBlue, 0.92) },
          }}
        >
          Resolve in e-Manifest
        </Button>
      </Tooltip>
    );
  }

  // One home for every card action: quiet utility links on the left, the
  // primary next step (or the resolved-state chip) on the right.
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      spacing={1}
      sx={{ flexWrap: "wrap" }}
    >
      <Stack direction="row" alignItems="center" spacing={0.5}>
        {viewDoc}
        <EmanifestLinkButton item={item} />
      </Stack>
      {trailing}
    </Stack>
  );
}

export function ComplianceCard(props: ComplianceCardProps) {
  const { item, now, activePreviewDocId } = props;
  const theme = useTheme();
  const isPreviewedDoc =
    activePreviewDocId != null &&
    item.sourceDocumentId != null &&
    activePreviewDocId === item.sourceDocumentId;

  return (
    <Card
      variant="outlined"
      sx={{
        // Symmetric-ish now that the short overline kind label is gone.
        pt: 1.5,
        pb: 2,
        px: 2,
        borderRadius: 2,
        bgcolor: isPreviewedDoc ? alpha(FIGMA_PRIMARY_BLUE, 0.08) : "background.paper",
        borderColor: isPreviewedDoc ? alpha(FIGMA_PRIMARY_BLUE, 0.5) : theme.palette.divider,
        "& > * + *": { mt: 1 },
        transition: "background-color 120ms, border-color 120ms",
      }}
    >
      <HeaderRow item={item} now={now} />
      {item.kind === "missing" ? (
        <MissingBody
          item={item}
          now={now}
          onAddProofOfContact={props.onAddProofOfContact}
          onCreateExceptionReport={props.onCreateExceptionReport}
          creatingExceptionReport={props.creatingExceptionReport}
        />
      ) : (
        <EpaBody item={item} />
      )}
      <Box>
        <ActionsRow
          item={item}
          isPreviewedDoc={isPreviewedDoc}
          onViewDocument={props.onViewDocument}
          onResolveInEManifest={props.onResolveInEManifest}
        />
      </Box>
    </Card>
  );
}
